import { useState } from 'react';
import type { ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, Download, Flag } from 'lucide-react';
import { PulseLoader } from './pulse-loader';

/**
 * Full-screen viewer for a chat photo. `src` is an object-URL produced by
 * `fetchAuthedBlob` + `URL.createObjectURL` (a bare `<img src>` can't carry the
 * bearer token). Works for photos you sent and photos you received:
 *
 *   <ImageViewer
 *     src={url}
 *     downloadName="nearmirer-photo-abc.jpg"
 *     onReport={reportable ? () => setReportOpen(true) : undefined}
 *     trigger={<button><img src={url} /></button>}
 *   />
 *
 * `Save` downloads the file; `Report` (only rendered when `onReport` is passed)
 * closes the viewer first, then hands off to the caller's report flow.
 */
export function ImageViewer({
  src,
  alt = 'Shared photo',
  downloadName = 'nearmirer-photo.jpg',
  trigger,
  onReport,
}: {
  src: string | null;
  alt?: string;
  downloadName?: string;
  trigger: ReactNode;
  onReport?: () => void;
}) {
  const [open, setOpen] = useState(false);

  const barButton =
    'inline-flex items-center gap-1.5 rounded-pill border-2 border-surface/70 bg-ink/40 px-3 py-1.5 text-xs font-semibold text-on-solid backdrop-blur-sm transition-colors hover:bg-ink/60';

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="dialog-overlay fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm" />
        <DialogPrimitive.Content className="image-viewer-content fixed inset-0 z-50 flex flex-col focus:outline-none">
          <DialogPrimitive.Title className="sr-only">{alt}</DialogPrimitive.Title>
          <div className="flex items-center justify-end gap-2 p-3">
            {onReport && (
              <button
                type="button"
                className={barButton}
                onClick={() => {
                  setOpen(false);
                  onReport();
                }}
              >
                <Flag className="size-3.5" strokeWidth={2.5} />
                Report
              </button>
            )}
            {src && (
              <a href={src} download={downloadName} className={barButton}>
                <Download className="size-3.5" strokeWidth={2.5} />
                Save
              </a>
            )}
            <DialogPrimitive.Close className={barButton} aria-label="Close">
              <X className="size-3.5" strokeWidth={2.5} />
            </DialogPrimitive.Close>
          </div>
          <div className="flex flex-1 items-center justify-center overflow-auto p-4 pt-0">
            {src ? (
              <img
                src={src}
                alt={alt}
                decoding="async"
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <PulseLoader />
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
